import * as cacheService from 'gocclr-shared-ui/dist/helpers/cache-helper';
import { generateUrlWithParams, cclFetch, ApiError, createIntegrationAppError } from 'gocclr-shared-ui/dist/helpers';
import { getJsonRequest } from './ccl-fetch.helper';
import { getApplicationPaths, getCultureInfoCode, getVersion } from '../app-context/accounts-context';

interface BaseContentResponse {
    __id: string;
    componentName: string;
    data: unknown;
    childComponents?: { [key: string]: BaseContentResponse };
}

export interface ContentResponse<T> extends BaseContentResponse {
    data: T;
}

const componentsMemoryCache: Record<string, BaseContentResponse> = {};

function buildKey(itemId: string, cultureInfo: string) {
    return `${itemId.toLowerCase().replace('{', '').replace('}', '')}-${cultureInfo.toLowerCase()}`;
}

function handleChildComponents(response: BaseContentResponse, cultureInfo: string) {
    if (!response) {
        return;
    }

    if (!response.childComponents) {
        return;
    }

    const keys = Object.keys(response.childComponents);

    if (keys.length === 0) {
        return;
    }

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (!response.childComponents[key]) {
            continue;
        }

        const child = response.childComponents[key];
        componentsMemoryCache[buildKey(child.__id, cultureInfo)] = child;
        handleChildComponents(child, cultureInfo);
    }
}

function getResponseFromContent<T>(url: string, cultureInfo: string): Promise<ContentResponse<T>> {
    if (cacheService.hasData(url)) {
        return Promise.resolve(cacheService.getData<ContentResponse<T>>(url)!);
    }

    if (cacheService.hasPromise(url)) {
        return cacheService.getPromise<ContentResponse<T>>(url)!.then(c => c);
    }

    const promise = cclFetch<ContentResponse<T>>(url, getJsonRequest('GET'))
        .then(response => {
            return response;
        }).then((contentResponse: ContentResponse<T>) => {
            cacheService.removePromise(url);
            cacheService.setData(url, contentResponse);

            componentsMemoryCache[buildKey(contentResponse.__id, cultureInfo)] = contentResponse;
            handleChildComponents(contentResponse, cultureInfo);

            return contentResponse;
        }).catch((error: ApiError) => {
            throw createIntegrationAppError(error);
        });

    cacheService.setPromise(url, promise);
    return promise;
}

export function getContent<T>(itemId: string): Promise<T> {
    const applicationPaths = getApplicationPaths();
    const cultureInfo = getCultureInfoCode();

    if (componentsMemoryCache[buildKey(itemId, cultureInfo)]) {
        return Promise.resolve(componentsMemoryCache[buildKey(itemId, cultureInfo)].data as T);
    }

    const url = generateUrlWithParams(applicationPaths.content, { id: itemId, language: cultureInfo, version: getVersion() });
    return getResponseFromContent<T>(url, cultureInfo).then(c => c.data);
}

